import { createApi } from '@reduxjs/toolkit/query/react';
import {
	collection,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	Timestamp,
	where,
} from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import { AUDIT_ACTIONS_COLLECTION } from '@/features/auditActions/auditActions';

// Deal/client timeline events (separate from profile auditActions).
export const ACTIVITIES_COLLECTION = 'activities';

// Combined filter options for both auditActions.actionType and activities.activityType.
export const AUDIT_ACTION_TYPES = [
	'userUpdated',
	'newClaimCreated',
	'newBusinessCreated',
	'newEndorsementCreated',
	'newRenewalCreated',
	'newDebtorCreated',
	'newProspectCreated',
	'dealArchived',
	'dealUnarchived',
	'dealDeleted',
	'clientNameChange',
	'ClaimClosed',
	'ClaimReopened',
	'prospectToClient',
	'clientToProspect',
];

const parseBoundary = (value, endOfDay = false) => {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	if (endOfDay) {
		date.setHours(23, 59, 59, 999);
	} else {
		date.setHours(0, 0, 0, 0);
	}
	return Timestamp.fromDate(date);
};

// Normalize a user/{id} doc into the performedBy shape used by the Audit table.
const mapUserSnapshot = (userSnap) => {
	if (!userSnap?.exists?.()) return null;
	const data = userSnap.data();
	return {
		userId: userSnap.id,
		hubspotId: data.hubspotId ?? null,
		name: data.name ?? null,
		email: data.email ?? null,
	};
};

// Activities store completedBy as a DocumentReference — fetch each unique user once.
const resolveCompletedByUsers = async (activityDocs) => {
	const refByPath = new Map();

	for (const docSnap of activityDocs) {
		const completedBy = docSnap.data()?.completedBy;
		if (completedBy?.path && !refByPath.has(completedBy.path)) {
			refByPath.set(completedBy.path, completedBy);
		}
	}

	const userByPath = new Map();

	await Promise.all(
		[...refByPath.entries()].map(async ([path, ref]) => {
			try {
				const userSnap = await getDoc(ref);
				const user = mapUserSnapshot(userSnap);
				if (user) {
					userByPath.set(path, user);
				}
			} catch (error) {
				console.error('[resolveCompletedByUsers]', path, error);
			}
		}),
	);

	return userByPath;
};

const mapAuditDoc = (docSnap) => {
	const data = docSnap.data();
	return {
		id: docSnap.id,
		source: 'auditAction',
		timestamp: data.timestamp?.toDate?.()?.toISOString?.() ?? null,
		actionType: data.actionType ?? null,
		// Profile-only fields; no client/deal on auditActions rows.
		fieldChanged: data.fieldChanged ?? null,
		previousValue: data.previousValue ?? null,
		newValue: data.newValue ?? null,
		performedBy: data.performedBy ?? null,
		targetUser: data.targetUser ?? null,
		clientId: null,
		clientName: null,
		dealId: null,
		dealName: null,
	};
};

// Map activities into the same row shape as auditActions for one shared table.
const mapActivityDoc = (docSnap, userByPath) => {
	const data = docSnap.data();
	const completedByPath = data.completedBy?.path;

	return {
		id: `activity-${docSnap.id}`, // Prefix avoids ID collisions with auditActions.
		source: 'activity',
		timestamp: data.createdDate?.toDate?.()?.toISOString?.() ?? null,
		actionType: data.activityType ?? null,
		fieldChanged: null,
		previousValue: null,
		newValue: null,
		performedBy: completedByPath ? userByPath.get(completedByPath) ?? null : null,
		targetUser: null,
		clientId: data.clientId != null ? String(data.clientId) : null,
		clientName: null,
		dealId: data.dealId != null ? String(data.dealId) : null,
		dealName: null,
	};
};

const sortRowsByTimestamp = (rows) =>
	[...rows].sort((a, b) => {
		const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
		const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
		return bTime - aTime;
	});

const filterRows = ({ rows, userId, userIds = [], actionTypes }) => {
	let filtered = rows;

	const resolvedUserIds = userIds.length > 0 ? userIds : userId != null ? [userId] : [];

	if (resolvedUserIds.length) {
		const allowed = new Set(resolvedUserIds.map(String));
		filtered = filtered.filter((row) => allowed.has(String(row.performedBy?.userId)));
	}

	if (actionTypes?.length) {
		const allowedTypes = new Set(actionTypes);
		filtered = filtered.filter((row) => allowedTypes.has(row.actionType));
	}

	return filtered;
};

export const auditApi = createApi({
	reducerPath: 'auditApi',
	tagTypes: ['AUDIT_ACTIONS'],
	endpoints: (builder) => ({
		searchAuditActions: builder.query({
			queryFn: async ({
				dateFrom,
				dateTo,
				userIds = [],
				actionTypes = [],
				userId,
			}) => {
				try {
					const from = parseBoundary(dateFrom, false);
					const to = parseBoundary(dateTo, true);

					if (!from || !to) {
						return { error: { status: 400, data: 'dateFrom and dateTo are required' } };
					}

					// Query both collections independently by their respective date fields.
					const auditQuery = query(
						collection(firestore, AUDIT_ACTIONS_COLLECTION),
						where('timestamp', '>=', from),
						where('timestamp', '<=', to),
						orderBy('timestamp', 'desc'),
						limit(500),
					);

					const activitiesQuery = query(
						collection(firestore, ACTIVITIES_COLLECTION),
						where('createdDate', '>=', from),
						where('createdDate', '<=', to),
						orderBy('createdDate', 'desc'),
						limit(500),
					);

					const [auditSnapshot, activitiesSnapshot] = await Promise.all([
						getDocs(auditQuery),
						getDocs(activitiesQuery),
					]);

					const userByPath = await resolveCompletedByUsers(activitiesSnapshot.docs);

					const auditRows = auditSnapshot.docs.map(mapAuditDoc);
					const activityRows = activitiesSnapshot.docs.map((docSnap) =>
						mapActivityDoc(docSnap, userByPath),
					);

					// Merge, sort, then apply shared user/type filters.
					const mergedRows = sortRowsByTimestamp([...auditRows, ...activityRows]);
					const data = filterRows({
						rows: mergedRows,
						userId,
						userIds,
						actionTypes,
					});

					const hasMore =
						auditSnapshot.docs.length >= 500 || activitiesSnapshot.docs.length >= 500;

					return { data: { data, hasMore } };
				} catch (error) {
					console.error('[searchAuditActions]', error);
					return { error: { status: 500, data: error.message } };
				}
			},
			providesTags: ['AUDIT_ACTIONS'],
		}),
	}),
});

export const { useSearchAuditActionsQuery, useLazySearchAuditActionsQuery } = auditApi;
